<template>
  <p
    class="tw:flex tw:gap-0"
    data-testid="totals-by-status"
  >
    <!-- having span elements below in separate lines causes a space to be added in HTML between them -->
    <!-- as a result, there's a space before the comma; gap-0 class prevents this space from being visible -->
    <template
      v-for="(totalData, index) in displayableTotalsByStatus"
      :key="totalData.status"
    >
      <span
        :class="totalData.class"
        :data-testid="`${totalData.status}-total`"
      >
        {{ totalText(totalData) }}
        <BaseStreemTooltip>{{ totalTooltip(totalData) }}</BaseStreemTooltip>
      </span>
      <span v-if="index < displayableTotalsByStatus.length - 1">,&nbsp;</span>
    </template>
  </p>
</template>

<script setup lang="ts">
import { countBy } from "lodash-es";
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseStreemTooltip } from "shared/components/base";

import { type UrlData } from "../shared/types";
import { UrlStatus } from "../useAddExternalItemsWorkflow/useUrlVerificationNotificationsSubscription";

import { URL_STATUSES_UI_CONFIG } from "./shared";

defineOptions({
  name: "UrlsTotalsByStatus",
});

export type UrlsTotalsByStatusProps = {
  urlsData: UrlData[];
};

const props = defineProps<UrlsTotalsByStatusProps>();

type TotalsByStatus = { [key in UrlStatus]: number };

const totalsByStatus = computed<TotalsByStatus>(
  () => countBy(props.urlsData, (urlData) => urlData.status) as TotalsByStatus
);

interface UrlsTotalForStatus {
  class: string;
  status: UrlStatus;
  value: number;
}

const displayableTotalsByStatus = computed<UrlsTotalForStatus[]>(() =>
  [...URL_STATUSES_UI_CONFIG.entries()]
    .map(([status, css_class]) => ({
      status,
      class: css_class,
      value: totalsByStatus.value[status],
    }))
    .filter((totalData) => totalData.value > 0)
);

function totalText(totalData: UrlsTotalForStatus): string {
  return getLocaleText(
    `external_items_bulk_add_modal.total.${totalData.status}${pluralitySuffix(totalData)}`,
    { urlsCount: totalData.value }
  );
}

function pluralitySuffix(totalData: UrlsTotalForStatus): string {
  if (totalData.status !== UrlStatus.cannotBeAdded) return "";

  return totalData.value === 1 ? "_singular" : "_plural";
}

function totalTooltip(totalData: UrlsTotalForStatus): string {
  return getLocaleText(
    `external_items_bulk_add_modal.total_tooltip.${totalData.status}`
  );
}
</script>
